import { cn } from "~/widgets/lib/utils";
import type {
  TimetableSession,
  TimetableLocation,
  TimetableTutor,
} from "~/widgets/lib/api";

// Course types constants
const COURSE_TYPES = {
  CLASS: "class",
  WORKSHOP: "workshop",
  COURSE: "course",
  TERMLY: "termly",
} as const;

type SessionCardProps = {
  session: TimetableSession;
  location?: TimetableLocation;
  tutor?: TimetableTutor;
  timezone: string;
  /** @deprecated Use CSS variables instead. Kept for backwards compatibility */
  primaryColor?: string;
  onBookNow: (session: TimetableSession) => void;
  onJoinWaitingList?: (session: TimetableSession) => void;
  isLoading?: boolean;
  showDateColumn?: boolean;
  londonLadyMode?: boolean;
};

// Format duration to human readable string (like "1hr 45min")
function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes}min`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (mins === 0) return `${hours}hrs`;
  return `${hours}hr ${mins}min`;
}

// Format date for display
function formatDate(
  dateString: string,
  timezone: string,
  use12h = false,
): {
  day: string;
  dayName: string;
  ordinal: string;
  time: string;
  time12h: string;
  fullDate: string;
} {
  const date = new Date(dateString);
  const options: Intl.DateTimeFormatOptions = { timeZone: timezone };

  const dayNum = date.toLocaleDateString("en-GB", {
    ...options,
    day: "numeric",
  });

  // Add ordinal suffix
  const n = parseInt(dayNum);
  const ordinal = n + (["th", "st", "nd", "rd"][(n % 100 > 10 && n % 100 < 14) ? 0 : n % 10] || "th");

  const dayName = date.toLocaleDateString("en-GB", {
    ...options,
    weekday: "short",
  });

  const time = date.toLocaleTimeString("en-GB", {
    ...options,
    hour: "numeric",
    minute: "2-digit",
    hour12: false,
  });

  const time12h = date
    .toLocaleTimeString("en-GB", {
      ...options,
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })
    .replace(" ", "")
    .toLowerCase();

  const fullDate = date.toLocaleDateString("en-GB", {
    ...options,
    day: "numeric",
    month: "short",
    year: "numeric",
  });

  return { day: dayNum, dayName, ordinal, time, time12h, fullDate };
}

// Get duration text based on course type
function getDurationText(session: TimetableSession, course: TimetableCourse): string | null {
  if (course.type === COURSE_TYPES.CLASS || course.type === COURSE_TYPES.WORKSHOP) {
    return formatDuration(session.duration);
  }
  if (course.type === COURSE_TYPES.TERMLY || course.type === COURSE_TYPES.COURSE) {
    return `${session.numberOfSessions} sessions`;
  }
  return null;
}

type TimetableCourse = TimetableSession["course"];

export function SessionCard({
  session,
  location,
  tutor,
  timezone,
  primaryColor,
  onBookNow,
  onJoinWaitingList,
  isLoading = false,
  showDateColumn = true,
  londonLadyMode = false,
}: SessionCardProps) {
  const course = session.course;
  const isBookable =
    session.bookingOptions?.length > 0 && session.availability > 0;
  const isSoldOut = session.availability <= 0;
  const dateInfo = formatDate(session.startDateTime, timezone);
  const hideAvailableTickets =
    course?.displaySettings?.hideDisplaysAvailableTickets ?? false;

  // Availability info
  let availabilityText = "";
  let availabilityColor = "";
  let dotColor = "";
  if (isSoldOut) {
    availabilityText = "SOLD OUT";
    availabilityColor = "text-red-600";
    dotColor = "bg-red-500";
  } else if (session.availability <= 3) {
    availabilityText = hideAvailableTickets
      ? "Few spots"
      : `${session.availability} spots left`;
    availabilityColor = "text-amber-600";
    dotColor = "bg-amber-500";
  } else if (session.availability > 0) {
    availabilityText = hideAvailableTickets
      ? "Available"
      : `${session.availability} spots left`;
    availabilityColor = "text-cyan-600";
    dotColor = "bg-cyan-500";
  }

  // Term sessions that aren't the first session (no pro rata) should show wait-list, not unavailable
  const isTermSessionNotBookable = session.isTerm && !isBookable && !isSoldOut;

  const handleClick = () => {
    if (!isLoading) {
      if (isSoldOut && onJoinWaitingList) {
        onJoinWaitingList(session);
        return;
      }
      if (isTermSessionNotBookable && onJoinWaitingList) {
        onJoinWaitingList(session);
        return;
      }
      onBookNow(session);
    }
  };

  // Button styling based on state
  const getButtonStyle = () => {
    if (isSoldOut) {
      return {
        className: londonLadyMode ? "bg-pink-200 text-gray-900" : "bg-pink-100 text-pink-700",
        text: londonLadyMode ? "Join\nWait-list" : "JOIN\nWAIT-LIST",
      };
    }
    if (isBookable) {
      return {
        className: londonLadyMode ? "" : "bg-primary text-primary-foreground",
        text: londonLadyMode ? "BOOK NOW" : "BOOK NOW",
        style: londonLadyMode ? { backgroundColor: "#c0f0ed" } : undefined,
      };
    }
    if (isTermSessionNotBookable) {
      return {
        className: londonLadyMode ? "bg-pink-200 text-gray-900" : "bg-pink-100 text-pink-700",
        text: londonLadyMode ? "Join\nWait-list" : "JOIN\nWAIT-LIST",
      };
    }
    return {
      className: "bg-muted text-muted-foreground",
      text: "UNAVAILABLE",
    };
  };

  const buttonStyle = getButtonStyle();
  const durationText = getDurationText(session, course);

  if (londonLadyMode) {
    return (
      <div
        className={cn(
          "flex bg-white border-b border-gray-200",
          !isSoldOut && "hover:bg-gray-50",
        )}
        style={{
          // minHeight inline (not min-h-[70px]): embedded host pages can mangle the
          // injected CSS and drop escaped selectors like .min-h-\[70px\], so the
          // base mobile layout must not depend on arbitrary-value classes.
          minHeight: 70,
          fontFamily:
            'var(--font-sans, "Founders Grotesk", -apple-system, BlinkMacSystemFont, sans-serif)',
        }}
      >
        {/* Time column */}
        <div className="shrink-0 w-14 md:w-16 border-r border-gray-200 flex items-center justify-center">
          <span className="text-sm text-center">{dateInfo.time12h}</span>
        </div>

        {/* Center: Title + Details */}
        <div className="flex-1 flex flex-col md:flex-row md:items-center px-2 md:px-3 py-2">
          <span className="text-base md:flex-[0_0_40%] md:pr-3">
            <FormatLondonLadyTitle title={course.title} />
          </span>
          <div className="text-base mt-1 md:mt-0">
            {session.numberOfSessions > 1 && course.type === "termly" && (
              <>
                <span>{session.numberOfSessions} session term</span>
                <span className="mx-2 text-gray-300 text-lg">•</span>
              </>
            )}
            {session.numberOfSessions > 1 && course.type === "course" && (
              <>
                <span>{session.numberOfSessions} session course</span>
                <span className="mx-2 text-gray-300 text-lg">•</span>
              </>
            )}
            <span className="font-bold">{availabilityText}</span>
          </div>
        </div>

        {/* Action column */}
        <div
          className={cn(
            "shrink-0 w-20 md:w-24 border-l border-gray-200 flex flex-col items-center justify-center text-center cursor-pointer",
            isBookable
              ? "bg-primary text-primary-foreground"
              : "bg-secondary text-secondary-foreground",
          )}
          onClick={handleClick}
        >
          {isLoading ? (
            <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
          ) : (
            <span className="text-sm md:text-base font-medium uppercase leading-tight whitespace-pre-line tracking-tight">
              {buttonStyle.text}
            </span>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex gap-3 px-4 py-3 bg-white border-b border-gray-100 transition-colors",
        "items-start md:items-center",
        !isSoldOut && "hover:bg-gray-50",
        isSoldOut && "bg-red-50/30",
      )}
      style={{
        fontFamily:
          'var(--font-sans, "Founders Grotesk", -apple-system, BlinkMacSystemFont, sans-serif)',
      }}
    >
      {/* Date column */}
      {showDateColumn && (
        <div className="shrink-0 w-10 text-center pt-0.5 md:pt-0">
          <div className="text-sm font-semibold text-gray-900">{dateInfo.ordinal}</div>
          <div className="text-xs text-gray-500">{dateInfo.dayName}</div>
        </div>
      )}

      {/* MOBILE LAYOUT: Stacked content */}
      <div className="flex-1 min-w-0 md:hidden">
        {/* Row 1: Time + Title */}
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="text-sm font-medium text-gray-900 shrink-0">
            {dateInfo.time}
          </span>
          <span className="text-gray-400">-</span>
          <span className="text-sm text-gray-900 break-words">
            {course.title}
            {session.title && (
              <span className="text-gray-500"> - {session.title}</span>
            )}
          </span>
        </div>

        {/* Row 2: Location + Duration */}
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-1 text-sm text-gray-500">
          {location && (
            <>
              <span>{location.name}</span>
              {durationText && <span className="text-gray-300">•</span>}
            </>
          )}
          {durationText && <span>{durationText}</span>}
        </div>

        {/* Row 3: Availability indicator */}
        <div className="flex items-center gap-1.5 mt-1">
          <span className={cn("w-2 h-2 rounded-full shrink-0", dotColor)} />
          <span className={cn("text-xs font-medium", availabilityColor)}>
            {availabilityText}
          </span>
        </div>

        {/* Row 4: Tutor (if available) */}
        {tutor && (
          <div className="mt-1 text-sm text-gray-600">
            {tutor.fullName || tutor.name}
          </div>
        )}
      </div>

      {/* DESKTOP LAYOUT: Horizontal columns */}
      {/* Time + Title */}
      <div className="hidden md:flex flex-1 min-w-0 items-baseline gap-2">
        <span className="text-sm font-medium text-gray-900 shrink-0">
          {dateInfo.time}
        </span>
        <span className="text-gray-400">-</span>
        <span className="text-sm text-gray-900 truncate">
          {course.title}
          {session.title && (
            <span className="text-gray-500"> - {session.title}</span>
          )}
        </span>
      </div>

      {/* Location */}
      <div className="hidden md:block shrink-0 text-sm w-28 truncate">
        {location ? (
          <span className="text-gray-600">{location.name}</span>
        ) : (
          <span className="text-gray-300">—</span>
        )}
      </div>

      {/* Duration */}
      <div className="hidden md:block shrink-0 text-sm w-24">
        {durationText ? (
          <span className="text-gray-500">{durationText}</span>
        ) : (
          <span className="text-gray-300">—</span>
        )}
      </div>

      {/* Availability indicator */}
      <div className="hidden md:flex items-center gap-1.5 shrink-0 w-28">
        <span className={cn("w-2 h-2 rounded-full shrink-0", dotColor)} />
        <span className={cn("text-xs font-medium", availabilityColor)}>
          {availabilityText}
        </span>
      </div>

      {/* Tutor */}
      <div className="hidden lg:block shrink-0 text-sm w-32 truncate">
        {tutor ? (
          <span className="text-gray-600">{tutor.fullName || tutor.name}</span>
        ) : (
          <span className="text-gray-300">—</span>
        )}
      </div>

      {/* Action button */}
      <div className="shrink-0 self-center">
        <button
          onClick={handleClick}
          disabled={isLoading}
          className={cn(
            // Stable hook class for dashboard "Custom CSS" overrides - do not rename
            "baluu-timetable-session-button",
            "px-3 py-2 md:px-4 rounded text-xs font-bold uppercase tracking-wide transition-all min-w-20 md:min-w-24 text-center whitespace-pre-line leading-tight",
            !isLoading && "hover:opacity-90 active:scale-95",
            isLoading && "opacity-50 cursor-wait",
            buttonStyle.className,
          )}
        >
          {isLoading ? (
            <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mx-auto" />
          ) : (
            buttonStyle.text
          )}
        </button>
      </div>
    </div>
  );
}

function FormatLondonLadyTitle({ title }: { title: string }) {
  if (title.includes("Shoreditch SweetArt Experience")) {
    return (
      <span>
        <b>Shoreditch</b>
        <span className="ml-1">SweetArt Experience</span>
      </span>
    );
  }
  if (title.includes("Camden Secrets Experience")) {
    return (
      <span>
        <b>Camden</b>
        <span className="ml-1">Secrets Experience</span>
      </span>
    );
  }
  return <>{title}</>;
}

// Date header component for grouping sessions
export function DateHeader({
  dateString,
  timezone,
  londonLadyMode = false,
}: {
  dateString: string;
  timezone: string;
  londonLadyMode?: boolean;
}) {
  const date = new Date(dateString);

  if (londonLadyMode) {
    const ddmmyyyy = date.toLocaleDateString("en-GB", {
      timeZone: timezone,
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
    const weekday = date.toLocaleDateString("en-GB", {
      timeZone: timezone,
      weekday: "long",
    });

    return (
      <div
        className="px-3 py-2 bg-white border border-gray-200 rounded-t"
        style={{
          fontFamily:
            'var(--font-sans, "Founders Grotesk", -apple-system, BlinkMacSystemFont, sans-serif)',
        }}
      >
        <h3 className="text-xl md:text-2xl font-medium italic text-gray-900 tracking-tight">
          {ddmmyyyy} - {weekday}
        </h3>
      </div>
    );
  }

  const formatted = date.toLocaleDateString("en-GB", {
    timeZone: timezone,
    weekday: "short",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  return (
    <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
      <h3 className="text-base font-semibold text-gray-900">{formatted}</h3>
    </div>
  );
}
